import { useTranslation } from 'react-i18next';
import CardItemView from '@/components/CardItemView';
import Select from '@/components/Select';
import Sort from '@/components/Sort';
import Button from '@/components/Button';
import { CardSkeleton } from '@/components/Skeleton';

const BrandCardsSection = ({ 
  cards, 
  categories,
  selectedCategory,
  onCategoryChange,
  onSortChange,
  onLoadMore,
  hasMoreCards,
  isLoading,
  isLoadingMore,
  brandInfo,
  pagesLoaded
}) => {
  const { t } = useTranslation();
  const brandOriginSearch = '?origin=brand';

  return (
    <div className="mb-12">
      <div className="flex justify-between items-center mb-8">
        <div className="flex items-center gap-4">
          <Select
            value={selectedCategory}
            onValueChange={onCategoryChange}
            options={categories}
            className="w-48"
          />
        </div>

        <Sort
          items={[
            { label: t('home.filters.sort.release_date'), value: 'date' },
            { label: t('home.filters.sort.price'), value: 'price' },
            { label: t('home.filters.sort.popularity'), value: 'popularity' },
          ]}
          onChange={onSortChange}
          defaultSortIndex={2}
          defaultDirection="desc"
        />
      </div>

      {isLoading && cards.length === 0 ? (
        <div className="grid grid-cols-5 max:grid-cols-6 gap-6">
          {Array.from({ length: 20 }, (_, i) => (
            <CardSkeleton key={`skeleton-${i}`} />
          ))}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-5 max:grid-cols-6 gap-6">
            {cards.map((card) => (
              <CardItemView 
                key={card.id} 
                id={card.id}
                name={card.localizedRawName}
                type={card.type === 'SINGLE_CARD' ? 'PSA_SINGLE_CARD' : 'CARD_BOX'}
                image={card.images?.[0]?.url}
                brand={card?.brand?.localizedName}
                price={(() => {
                  const vals = [
                    card.stats?.lowestPriceOnSale,
                    card.stats?.lowestPriceOnSaleOfficial,
                  ].filter(v => typeof v === 'number' && v > 0);
                  return vals.length ? Math.min(...vals) : 0;
                })()}
                orders={card.stats?.salesVolume}
                code={card.code}
                rawCode={card.rawCode}
                set={card.set}
                rarityInfo={card.rarityInfo}
                linkTo={brandInfo ? `/card/${card.id}${brandOriginSearch}` : null}
              />
            ))}
            
            {/* Show skeleton cards when loading more */}
            {isLoadingMore && Array.from({ length: 40 }, (_, i) => (
              <CardSkeleton key={`skeleton-${i}`} />
            ))}
          </div>

          {cards.length === 0 && !isLoading && !isLoadingMore && (
            <div className="flex justify-center items-center h-64">
              <p>{t('brand.no_cards')}</p>
            </div>
          )}
          
          {/* Show load more button after 3 pages */}
          {pagesLoaded >= 1 && hasMoreCards && !isLoadingMore && (
            <div className="flex justify-center mt-8">
              <Button
                onClick={onLoadMore}
                variant="default"
                size="l"
              >
                {t('home.actions.load_more')}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BrandCardsSection; 
